import { userClickAtmLimitsAction, userClickBillingAddressAction, userClickCancelCardAction, userToggleLockCardAction, userClickNotificationsPreferencesAction, userClickResetPinAction, userToggleCardDetailsAction, userClickOrderCardAction } from '@banking/ui/workflows/card-settings';
import { Card, CardFulfillment, CardStatus, Relationship } from '@square/dashboard-protos/models/squareup/bbdebit/cardmanagement';
import { ControlType } from '@banking/ui/components/banking/checking/card-settings/management/setting';
import assert from '@banking/ui/utils/assert';
import productNameTranslationKey from '@banking/ui/utils/checking/product-name-translation-key';

/**
 * These are the status in which the card is activated and the majority of settings
 * are accessible to the user.
 */
const ACTIVE_CARD_STATES = new Set([CardStatus.CARD_STATUS_ACTIVE, CardStatus.CARD_STATUS_LOCKED]);
export const isCardInActiveState = status => {
  if (status == null) {
    return false;
  }
  return ACTIVE_CARD_STATES.has(status);
};
export const isCardExpiring = card => {
  return card.expirationData?.status === Card.Expiration.ExpirationStatus.EXPIRATION_STATUS_EXPIRING;
};
export const isCardExpired = card => {
  return card.expirationData?.status === Card.Expiration.ExpirationStatus.EXPIRATION_STATUS_EXPIRED;
};
export const hasExpiringCardScheduledReplacementDate = card => {
  return isCardExpiring(card) && Boolean(card?.expirationData?.replacementCard?.scheduledIssueDate);
};
export const hasCardReplacement = card => {
  return Boolean(card?.expirationData?.replacementCard?.id);
};
export const isReplacementCard = card => Boolean(card.previousCardId);
export const isInactiveCard = card => {
  return [CardStatus.CARD_STATUS_ACTIVATABLE, CardStatus.CARD_STATUS_ISSUED, CardStatus.CARD_STATUS_ORDERED].includes(card.status);
};
export const isInactiveReplacementCard = card => {
  return isReplacementCard(card) && isInactiveCard(card);
};
export const isCardDelivered = card => {
  return card.fulfillment?.status === CardFulfillment.FulfillmentStatus.FULFILLMENT_STATUS_DELIVERED;
};
export const isCardActivatable = card => {
  return card.status === CardStatus.CARD_STATUS_ACTIVATABLE;
};
export const isCardTerminated = card => {
  return card.status === CardStatus.CARD_STATUS_TERMINATED;
};
export const isCardHolder = card => {
  return card.relationship?.includes(Relationship.RELATIONSHIP_CARDHOLDER);
};
export const isAccountOwnerCard = card => {
  return card.relationship.includes(Relationship.RELATIONSHIP_ACCOUNT_HOLDER) && isCardHolder(card);
};
export const getAtmLimitsSetting = (emit, t) => state => {
  assert(state.card != null);
  const {
    card
  } = state;
  const {
    status,
    relationship
  } = card;
  const shouldHideDueToStatus = !isCardInActiveState(status) && status !== CardStatus.CARD_STATUS_SUSPENDED;
  if (shouldHideDueToStatus || !relationship.includes(Relationship.RELATIONSHIP_ACCOUNT_HOLDER)) {
    return null;
  }
  return {
    icon: 'cash',
    text: t('banking.checking.cardSettings.page.settings.atmLimits'),
    control: {
      type: ControlType.Regular,
      action: () => emit(userClickAtmLimitsAction())
    }
  };
};
export const getBillingAddressSetting = (emit, t) => state => {
  assert(state.card != null);
  const {
    status,
    relationship
  } = state.card;
  if (status === CardStatus.CARD_STATUS_TERMINATED || status === CardStatus.CARD_STATUS_SUSPENDED || isCardExpired(state.card) || !relationship.includes(Relationship.RELATIONSHIP_ACCOUNT_HOLDER)) {
    return null;
  }
  return {
    icon: 'misc-location-pin',
    text: t('banking.checking.cardSettings.page.settings.billingAddress'),
    control: {
      type: ControlType.Regular,
      action: () => emit(userClickBillingAddressAction())
    }
  };
};
export const getNotificationPreferencesSetting = (emit, t) => state => {
  const card = state.card;
  assert(card != null);
  if (card.status === CardStatus.CARD_STATUS_TERMINATED || !card.relationship.includes(Relationship.RELATIONSHIP_ACCOUNT_HOLDER)) {
    return null;
  }
  return {
    icon: 'action-bell',
    text: t('banking.checking.cardSettings.page.settings.notificationPreferences'),
    control: {
      type: ControlType.Regular,
      // TODO(cvu): add action when ready
      action: () => emit(userClickNotificationsPreferencesAction())
    }
  };
};
export const getCancelCardSetting = (emit, t) => state => {
  assert(state.card != null);
  if (state.card.status === CardStatus.CARD_STATUS_TERMINATED || state.card.relationship.includes(Relationship.RELATIONSHIP_CARDHOLDER)) {
    return null;
  }
  return {
    icon: 'alert-x-circle',
    text: t('banking.checking.cardSettings.page.settings.cancelCard'),
    control: {
      type: ControlType.Regular,
      action: () => emit(userClickCancelCardAction())
    }
  };
};
export const getToggleCardSetting = (emit, t) => state => {
  const card = state.card;
  assert(card != null);
  const {
    status
  } = card;
  if (!isCardInActiveState(status) || isCardExpired(card)) {
    return null;
  }
  return {
    icon: status === CardStatus.CARD_STATUS_LOCKED ? 'action-lock-off' : 'action-lock-on',
    text: status === CardStatus.CARD_STATUS_LOCKED ? t('banking.checking.cardSettings.page.settings.toggleLockCard.unlock') : t('banking.checking.cardSettings.page.settings.toggleLockCard.lock'),
    control: {
      type: ControlType.Toggle,
      value: status === CardStatus.CARD_STATUS_LOCKED,
      loading: state.toggleLoading.lockCard,
      action: () => emit(userToggleLockCardAction(!(status === CardStatus.CARD_STATUS_LOCKED)))
    }
  };
};
export const getToggleCardDetailsSetting = (emit, t) => state => {
  const card = state.card;
  assert(card);
  const {
    status,
    relationship,
    canDigitalSpend
  } = state.card;
  if (status === CardStatus.CARD_STATUS_TERMINATED || status === CardStatus.CARD_STATUS_SUSPENDED || !relationship.includes(Relationship.RELATIONSHIP_CARDHOLDER) || !canDigitalSpend) {
    return null;
  }
  return {
    icon: '24px-eye',
    text: t('banking.checking.cardSettings.actions.showCardDetails'),
    control: {
      type: ControlType.Toggle,
      value: state.showCardDetails,
      loading: state.toggleLoading.showCardDetails,
      action: () => emit(userToggleCardDetailsAction(!state.showCardDetails))
    }
  };
};
export const getResetPinSetting = (emit, t) => state => {
  assert(state.card != null);
  const {
    status,
    relationship
  } = state.card;
  if (status === CardStatus.CARD_STATUS_TERMINATED || status === CardStatus.CARD_STATUS_SUSPENDED || isCardExpired(state.card) || !relationship.includes(Relationship.RELATIONSHIP_CARDHOLDER)) {
    return null;
  }
  return {
    icon: 'keyboard',
    text: t('banking.checking.cardSettings.page.settings.resetPin'),
    control: {
      type: ControlType.Regular,
      action: () => emit(userClickResetPinAction())
    }
  };
};
export const getCloseAccountSetting = (emit, t, productName) => state => {
  assert(state.card != null);
  if (state.card.status !== CardStatus.CARD_STATUS_TERMINATED || !state.card.relationship.includes(Relationship.RELATIONSHIP_CARDHOLDER)) {
    return null;
  }
  return {
    icon: 'alert-x-circle',
    text: t(productNameTranslationKey(productName, 'banking.checking.cardSettings.page.settings.closeAccount')),
    control: {
      type: ControlType.Regular,
      action: () => emit(userClickCancelCardAction())
    }
  };
};
export const getOrderCardSetting = (emit, t, productName) => state => {
  assert(state.card != null);
  if (state.card.status !== CardStatus.CARD_STATUS_TERMINATED) {
    return null;
  }
  return {
    icon: '24px-debit-card',
    text: t(productNameTranslationKey(productName, 'banking.checking.cardSettings.page.settings.orderCard')),
    control: {
      type: ControlType.Regular,
      action: () => emit(userClickOrderCardAction())
    }
  };
};
export const getCardSettings = (emit, t, productName) => state => {
  if (state.loading) {
    return [];
  }
  return [getToggleCardDetailsSetting(emit, t)(state), getToggleCardSetting(emit, t)(state), getNotificationPreferencesSetting(emit, t)(state), getBillingAddressSetting(emit, t)(state), getAtmLimitsSetting(emit, t)(state), getResetPinSetting(emit, t)(state), getCancelCardSetting(emit, t)(state), getCloseAccountSetting(emit, t, productName)(state), getOrderCardSetting(emit, t, productName)(state)].filter(setting => setting !== null);
};